import { cn } from "@shadcn/lib/utils";
import { PropsWithChildren, useCallback, useRef } from "react";

type PageContainerProps = {
	footer?: React.ReactNode;
	className?: string;
	footerStyles?: string;
	reserveFooterSpace?: boolean;
};

export const PageContainer = (props: PropsWithChildren<PageContainerProps>) => {
	const footerRef = useRef<HTMLDivElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const reserveFooterSpace = props.reserveFooterSpace ?? true;

	const handleSize = useCallback(() => {
		if (!containerRef.current) return;
		if (!reserveFooterSpace) {
			containerRef.current.style.paddingBottom = "";
			return;
		}
		if (!footerRef.current) return;
		const height = footerRef.current.offsetHeight;
		const marginTop = getComputedStyle(footerRef.current).marginTop.replace("px", "");
		const marginBottom = getComputedStyle(footerRef.current).marginBottom.replace("px", "");
		containerRef.current.style.paddingBottom = height ? `${height + parseInt(marginTop) + parseInt(marginBottom)}px` : "";
	}, [reserveFooterSpace]);

	const handleContainer = useCallback(
		(el: HTMLDivElement | null) => {
			containerRef.current = el;
			handleSize();
		},
		[handleSize]
	);

	const handleFooter = useCallback(
		(el: HTMLDivElement | null) => {
			footerRef.current = el;
			if (!el) return;
			const observer = new MutationObserver(handleSize);
			observer.observe(el, { attributes: true, childList: true, subtree: true, characterData: true });
			handleSize();
			return () => {
				observer.disconnect();
			};
		},
		[handleSize]
	);

	return (
		<div ref={handleContainer} className={cn("flex flex-col gap-4", props.className)}>
			{props.children}
			{props.footer && (
				<div ref={handleFooter} className={props.footerStyles ?? "fixed bottom-0 left-0 right-0 m-6 md:static md:w-full"}>
					{props.footer}
				</div>
			)}
		</div>
	);
};
